import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Star, Upload, Loader2, X, CheckCircle2 } from "lucide-react";
import { z } from "zod";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SERVICES } from "@/lib/business";
import { buildBreadcrumbJsonLd, canonicalLink, pageSocialMeta, SITE_URL } from "@/lib/seo";

const BREADCRUMB_JSONLD = buildBreadcrumbJsonLd([
  { name: "Home", path: "/" },
  { name: "Reviews", path: "/reviews" },
]);

type Review = {
  id: string;
  customer_name: string;
  customer_location: string | null;
  rating: number;
  review_text: string;
  service_type: string | null;
  before_photo_url: string | null;
  after_photo_url: string | null;
  created_at: string;
};

async function fetchApprovedReviews(): Promise<Review[]> {
  const { data, error } = await supabase
    .from("customer_reviews")
    .select(
      "id, customer_name, customer_location, rating, review_text, service_type, before_photo_url, after_photo_url, created_at"
    )
    .eq("status", "approved")
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return data ?? [];
}

function buildReviewsJsonLd(reviews: Review[]) {
  if (reviews.length === 0) return null;

  const itemReviewed = {
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    name: "BLVDS Detailing",
    url: SITE_URL,
  };

  const ratingSum = reviews.reduce((sum, r) => sum + r.rating, 0);
  const ratingValue = (ratingSum / reviews.length).toFixed(1);

  const aggregateRating = {
    "@context": "https://schema.org",
    "@type": "AggregateRating",
    itemReviewed,
    ratingValue,
    reviewCount: String(reviews.length),
    bestRating: "5",
    worstRating: "1",
  };

  const reviewNodes = reviews.slice(0, 20).map((r) => ({
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed,
    author: {
      "@type": "Person",
      name: r.customer_name,
      ...(r.customer_location ? { address: r.customer_location } : {}),
    },
    datePublished: r.created_at,
    reviewBody: r.review_text,
    reviewRating: {
      "@type": "Rating",
      ratingValue: String(r.rating),
      bestRating: "5",
      worstRating: "1",
    },
  }));

  return [aggregateRating, ...reviewNodes];
}

export const Route = createFileRoute("/reviews")({
  loader: async () => {
    try {
      const reviews = await fetchApprovedReviews();
      return { reviews };
    } catch {
      return { reviews: [] as Review[] };
    }
  },
  head: ({ loaderData }) => {
    const reviews = loaderData?.reviews ?? [];
    const jsonLd = buildReviewsJsonLd(reviews);
    return {
      meta: [
        { title: "Customer Reviews — BLVDS Detailing NYC" },
        {
          name: "description",
          content:
            "Real customer reviews and before/after photos from BLVDS Detailing — mobile car detailing in NYC serving Queens, Brooklyn & Long Island.",
        },
        ...pageSocialMeta(
          "/reviews",
          "Customer Reviews — BLVDS Detailing NYC",
          "Real before/after photos and reviews from BLVDS mobile car detailing customers across NYC — Queens, Brooklyn & Long Island.",
        ),
      ],
      links: [canonicalLink("/reviews")],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(BREADCRUMB_JSONLD) },
        ...(jsonLd
          ? [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }]
          : []),
      ],
    };
  },
  errorComponent: ({ error }) => (
    <Layout>
      <section className="py-20 max-w-3xl mx-auto px-4 text-center">
        <h1 className="text-3xl font-bold mb-3">Couldn't load reviews</h1>
        <p className="text-muted-foreground">{error.message}</p>
      </section>
    </Layout>
  ),
  notFoundComponent: () => (
    <Layout>
      <section className="py-20 max-w-3xl mx-auto px-4 text-center">
        <h1 className="text-3xl font-bold">Page not found</h1>
      </section>
    </Layout>
  ),
  component: ReviewsPage,
});

const reviewSchema = z.object({
  customer_name: z.string().trim().min(1, "Name is required").max(80),
  customer_location: z.string().trim().max(100).optional(),
  rating: z.number().int().min(1).max(5),
  review_text: z.string().trim().min(10, "Review must be at least 10 characters").max(1000),
  service_type: z.string().max(50).optional(),
});

const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5MB

function ReviewsPage() {
  const { reviews: initialReviews } = Route.useLoaderData();
  const [reviews, setReviews] = useState<Review[]>(initialReviews);

  // Refresh client-side in case loader data is stale.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const fresh = await fetchApprovedReviews();
        if (active) setReviews(fresh);
      } catch {
        // keep loader data
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-20 pb-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">
            Customer Reviews
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            The streets <span className="text-gradient-accent pr-2">don't lie</span>.
          </h1>
          <p className="text-muted-foreground">
            Drop your before & after, rate your experience, and show NYC what clean looks like.
          </p>
        </div>
      </section>

      {/* Submit form */}
      <section className="py-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <ReviewForm
          onSubmitted={() => {
            toast.success("Thanks! Your review will be visible after approval.");
          }}
        />
      </section>

      {/* Approved reviews list */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">What customers are saying</h2>
        </div>
        {reviews.length === 0 ? (
          <p className="text-center text-muted-foreground py-16">
            No reviews yet. Be the first to leave one above!
          </p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {reviews.map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const hasPhotos = review.before_photo_url || review.after_photo_url;
  return (
    <div className="p-6 rounded-2xl bg-surface border border-border">
      <div className="flex gap-1 mb-3">
        {Array.from({ length: review.rating }).map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-[#FFA500] text-[#FFA500]" />
        ))}
      </div>
      <p className="text-foreground/90 mb-4">{review.review_text}</p>
      {hasPhotos && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <PhotoTile url={review.before_photo_url} label="Before" />
          <PhotoTile url={review.after_photo_url} label="After" />
        </div>
      )}
      <div className="text-sm">
        <span className="font-semibold">{review.customer_name}</span>
        {review.customer_location && (
          <span className="text-muted-foreground"> · {review.customer_location}</span>
        )}
        {review.service_type && (
          <span className="text-muted-foreground"> · {review.service_type}</span>
        )}
      </div>
    </div>
  );
}

function PhotoTile({ url, label }: { url: string | null; label: string }) {
  const [resolved, setResolved] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!url) {
      setResolved(null);
      return;
    }
    if (/^https?:\/\//i.test(url)) {
      setResolved(url);
      return;
    }
    (async () => {
      const { data } = await supabase.storage
        .from("review-photos")
        .createSignedUrl(url, 60 * 60);
      if (active) setResolved(data?.signedUrl ?? null);
    })();
    return () => {
      active = false;
    };
  }, [url]);

  if (!url) {
    return (
      <div className="aspect-square rounded-lg border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
        No {label.toLowerCase()} photo
      </div>
    );
  }
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden border border-border">
      {resolved && (
        <img src={resolved} alt={`${label} detail`} className="w-full h-full object-cover" loading="lazy" />
      )}
      <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-background/80 backdrop-blur text-xs font-semibold uppercase tracking-wider">
        {label}
      </span>
    </div>
  );
}

function ReviewForm({ onSubmitted }: { onSubmitted: () => void }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [beforeFile, setBeforeFile] = useState<File | null>(null);
  const [afterFile, setAfterFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function uploadPhoto(file: File): Promise<string | null> {
    if (file.size > MAX_PHOTO_BYTES) {
      throw new Error(`Photo must be under 5MB (${file.name})`);
    }
    if (!file.type.startsWith("image/")) {
      throw new Error("Only image files allowed");
    }
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const allowed = ["jpg", "jpeg", "png", "webp", "gif"];
    if (!allowed.includes(ext)) {
      throw new Error("Only JPG, PNG, WEBP, or GIF images allowed");
    }
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("review-photos").upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
    if (error) throw error;
    return path;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Please select a star rating");
      return;
    }

    const parsed = reviewSchema.safeParse({
      customer_name: name,
      customer_location: location || undefined,
      rating,
      review_text: text,
      service_type: serviceType || undefined,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }

    setSubmitting(true);
    try {
      const [beforeUrl, afterUrl] = await Promise.all([
        beforeFile ? uploadPhoto(beforeFile) : Promise.resolve(null),
        afterFile ? uploadPhoto(afterFile) : Promise.resolve(null),
      ]);

      const { error } = await supabase.from("customer_reviews").insert({
        customer_name: parsed.data.customer_name,
        customer_location: parsed.data.customer_location ?? null,
        rating: parsed.data.rating,
        review_text: parsed.data.review_text,
        service_type: parsed.data.service_type ?? null,
        before_photo_url: beforeUrl,
        after_photo_url: afterUrl,
        status: "pending",
      });
      if (error) throw error;

      setDone(true);
      onSubmitted();
      setName("");
      setLocation("");
      setRating(0);
      setText("");
      setServiceType("");
      setBeforeFile(null);
      setAfterFile(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to submit review";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="p-8 rounded-2xl bg-surface border border-primary/30 text-center">
        <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-3" />
        <h3 className="text-2xl font-bold mb-2">Review submitted!</h3>
        <p className="text-muted-foreground mb-5">
          Thanks for sharing. We'll review and publish it shortly.
        </p>
        <Button variant="outline" onClick={() => setDone(false)} className="h-12 text-base">
          Leave another review
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="p-7 rounded-2xl bg-surface border border-border space-y-5"
    >
      <div>
        <h2 className="text-2xl font-bold mb-1">Leave a review</h2>
        <p className="text-sm text-muted-foreground">
          Got pics? Before & afters go a long way.
        </p>
      </div>

      {/* Star rating */}
      <div>
        <Label className="block mb-2">Your rating</Label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = (hover || rating) >= n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                className="inline-flex items-center justify-center w-12 h-12 rounded-md transition-smooth hover:scale-110"
              >
                <Star
                  className={
                    filled
                      ? "w-7 h-7 fill-[#FFA500] text-[#FFA500]"
                      : "w-7 h-7 text-muted-foreground"
                  }
                />
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Your name *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            required
          />
        </div>
        <div>
          <Label htmlFor="location">Location (e.g. Astoria, Queens)</Label>
          <Input
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={100}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="service">Service used</Label>
        <select
          id="service"
          value={serviceType}
          onChange={(e) => setServiceType(e.target.value)}
          className="flex h-12 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="">Select a service (optional)</option>
          {SERVICES.map((s) => (
            <option key={s.id} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <Label htmlFor="review">Your review *</Label>
        <Textarea
          id="review"
          value={text}
          onChange={(e) => setText(e.target.value)}
          minLength={10}
          maxLength={1000}
          rows={4}
          placeholder="How did we do? Any details that would help other customers?"
          required
        />
        <p className="text-xs text-muted-foreground mt-1">{text.length}/1000</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <PhotoUpload label="Before photo" file={beforeFile} setFile={setBeforeFile} />
        <PhotoUpload label="After photo" file={afterFile} setFile={setAfterFile} />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full h-12 bg-gradient-primary text-primary-foreground hover:shadow-glow text-base"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...
          </>
        ) : (
          "Submit review"
        )}
      </Button>
    </form>
  );
}

function PhotoUpload({
  label,
  file,
  setFile,
}: {
  label: string;
  file: File | null;
  setFile: (f: File | null) => void;
}) {
  const previewUrl = file ? URL.createObjectURL(file) : null;
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div>
      <Label className="block mb-2">{label}</Label>
      {previewUrl ? (
        <div className="relative aspect-square rounded-lg overflow-hidden border border-border">
          <img src={previewUrl} alt={`${label} preview`} className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => setFile(null)}
            className="absolute top-2 right-2 inline-flex items-center justify-center w-12 h-12 rounded-full bg-background/80 backdrop-blur hover:bg-background transition-smooth"
            aria-label={`Remove ${label}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center aspect-square rounded-lg border border-dashed border-border hover:border-primary/50 cursor-pointer transition-smooth text-muted-foreground hover:text-foreground">
          <Upload className="w-6 h-6 mb-2" />
          <span className="text-xs">Add {label.toLowerCase()}</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setFile(f);
            }}
          />
        </label>
      )}
    </div>
  );
}
